import { Cancel, CheckCircle } from '@mui/icons-material';
import { Box, Card, Typography } from '@mui/material';
import { sortBy } from 'lodash';
import { Fragment } from 'react';
import SortableList from '@/components/exercise/sortable-list';
import RichContent from '@/components/shared/rich-content';
import { themeSlug } from '@/constants/environment';
import { useQuestionMarking } from '@/hooks';
import { useCourses } from '@/providers';
import { useViewStore } from '@/stores';
import type { ContentItem, Question } from '@/types';
import { isExercise } from '@/types';

export default function AnswersSequence({
  question,
  result,
  preview,
}: {
  question: Question;
  result?: boolean;
  preview?: boolean;
}) {
  const { selectedCourse, selectedChapter } = useCourses();
  const { showExplanations } = useViewStore();
  const { id, answers, marking, solution } = question;
  const answersInMarkingSequence = sortBy(answers, (answer) =>
    marking.indexOf(answer.index.toString()),
  );
  const showExplanation = showExplanations.indexOf(id) > -1;
  const sortableAnswers: ContentItem[] = answersInMarkingSequence.map(
    ({ id, answerNode, index }) => ({
      id: `${id}-${index}`,
      content: answerNode,
      index,
    }),
  );
  const sequenceLabel = themeSlug === 'mympu' ? 'passend' : 'zutreffend';
  const correctSequenceLabel = themeSlug === 'mympu' ? 'Passende' : 'Zutreffende';

  const { updateMarking } = useQuestionMarking(selectedCourse?.id || '');

  const onDragEnd = (newMarkingSequence: number[]) => {
    if (!selectedChapter || !isExercise(selectedChapter)) return;
    updateMarking({
      question,
      update: newMarkingSequence.map((index) => index.toString()),
      exercise: selectedChapter,
    });
  };

  if (!result || preview) {
    return (
      <>
        <Typography sx={{ my: 2 }} variant="h6">
          Ist die Reihenfolge {sequenceLabel}?
        </Typography>
        {preview ? (
          sortableAnswers.map(({ id, content }) => (
            <Typography
              key={id}
              sx={{
                border: '1px dashed #fff',
                p: '10px 15px',
                mb: 2,
                opacity: 0.8,
              }}>
              <RichContent content={content} noParagraphMargin />
            </Typography>
          ))
        ) : (
          <SortableList answers={sortableAnswers} onDragEnd={onDragEnd} />
        )}
      </>
    );
  }

  // Results...
  const answersInSolutionSequence = solution
    ? sortBy(answers, (answer) => solution.indexOf(answer.index.toString()))
    : [];

  return (
    <>
      <Typography sx={{ my: 2 }} variant="h6">
        Gewählte Reihenfolge:
      </Typography>
      {answersInMarkingSequence.map(({ id, answerNode, index }, markingPosition) => {
        const correctPosition = solution ? solution.indexOf(index.toString()) : -1;
        const isInCorrectPosition = markingPosition === correctPosition;

        return (
          <Card
            variant="outlined"
            sx={{
              p: '10px 8px',
              mb: 1,
            }}
            key={`${id}-${index}`}>
            <Box
              sx={{
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: result ? (isInCorrectPosition ? '#16C36C' : '#FC5055') : undefined,
              }}>
              <RichContent content={answerNode} noParagraphMargin />
              {result &&
                (isInCorrectPosition ? (
                  <CheckCircle sx={{ color: '#16C36C', mt: 0.1 }} fontSize="small" />
                ) : (
                  <Cancel sx={{ color: '#FC5055', mt: 0.1 }} fontSize="small" />
                ))}
            </Box>
          </Card>
        );
      })}
      {showExplanation && (
        <>
          <Typography sx={{ my: 2 }} variant="h6">
            {correctSequenceLabel} Reihenfolge:
          </Typography>
          {answersInSolutionSequence.map(({ id, answerNode, index }) => (
            <Fragment key={`${id}-${index}`}>
              <Card
                variant="outlined"
                sx={{
                  p: '10px 8px',
                  mb: 1,
                }}>
                <Box
                  sx={{
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    color: '#16C36C',
                  }}>
                  <RichContent content={answerNode} noParagraphMargin />
                  <CheckCircle sx={{ color: '#16C36C' }} fontSize="small" />
                </Box>
              </Card>
            </Fragment>
          ))}
        </>
      )}
    </>
  );
}
